import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { AppAutocomplete, AppButton, AppConfirmDialog } from "@pro-sto/ui";
import type { SelectOption } from "@pro-sto/ui";
import {
  authStore,
  paymentSchemeTemplatesStore,
  servicesStore,
  toastStore,
} from "@/stores";
import { ROUTES } from "@/constants/routes";
import type {
  PaymentSchemeRateType,
  PaymentSchemeServiceRow,
  UpdatePaymentSchemeTemplatePayload,
} from "@/types/employee.types";
import { PaymentSchemeEditor } from "@/components/PaymentSchemeEditor";
import { ArrowLeftIcon, FloppyDiskIcon } from "@/components/icons";
import "./PaymentSchemeTemplateEditPage.css";

// Шаблон должен покрывать услуги ВСЕХ категорий (мойка, шиномонтаж и т.д.),
// а не только активную вкладку каталога — иначе часть услуг не попадёт в шаблон.
const catalogToRows = (): PaymentSchemeServiceRow[] =>
  servicesStore.allCategoriesServices.map((service) => ({
    service_uuid: service.uuid,
    name: service.name,
    rate_type: null,
    value: null,
    is_overridden: false,
  }));

const PaymentSchemeTemplateEditPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const isCreate = !uuid;
  const serviceCenterUuid = authStore.user?.service_center_uuid;

  const [name, setName] = useState("");
  const [fixPerShift, setFixPerShift] = useState<string | null>(null);
  const [hourlyRate, setHourlyRate] = useState<string | null>(null);
  const [serviceRows, setServiceRows] = useState<PaymentSchemeServiceRow[]>([]);
  const [nameError, setNameError] = useState<string | undefined>();
  const [pendingSavePayload, setPendingSavePayload] =
    useState<UpdatePaymentSchemeTemplatePayload | null>(null);

  useEffect(() => {
    if (!serviceCenterUuid) return;
    paymentSchemeTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
    servicesStore.fetchAllCategories();
  }, [serviceCenterUuid]);

  useEffect(() => {
    if (isCreate) {
      paymentSchemeTemplatesStore.clearActiveTemplate();
      setName("");
      setFixPerShift(null);
      setHourlyRate(null);
      return;
    }

    if (uuid) {
      paymentSchemeTemplatesStore.loadTemplateDetail(uuid);
    }
  }, [uuid, isCreate]);

  // Каталог услуг грузится асинхронно (fetchAllCategories в эффекте выше), поэтому
  // строки шаблона собираем отдельно и пересобираем, когда каталог обновится —
  // иначе при первом заходе в сессии на "Создать шаблон" список замирает пустым.
  useEffect(() => {
    if (isCreate) {
      setServiceRows(catalogToRows());
    }
  }, [isCreate, servicesStore.allCategoriesServices]);

  const activeTemplate = paymentSchemeTemplatesStore.activeTemplate;

  useEffect(() => {
    if (!isCreate && activeTemplate) {
      setName(activeTemplate.name);
      setFixPerShift(activeTemplate.fix_per_shift ?? null);
      setHourlyRate(activeTemplate.hourly_rate ?? null);
      setServiceRows(activeTemplate.services);
    }
  }, [activeTemplate, isCreate]);

  const templateOptions: SelectOption[] = paymentSchemeTemplatesStore.items
    .filter((t) => !t.is_archived)
    .map((t) => ({ label: t.name, value: t.uuid }));

  const autocompleteValue: SelectOption = uuid
    ? { label: name, value: uuid }
    : { label: name, value: null, isCustom: true };

  const handleTemplatePick = (option: SelectOption) => {
    if (option.value && option.value !== uuid) {
      navigate(
        generatePath(ROUTES.PAYMENT_SCHEME_TEMPLATE_EDIT, {
          uuid: option.value as string,
        }),
      );
      return;
    }
    setName(option.label);
    if (nameError) setNameError(undefined);
  };

  const handleCancel = () => navigate(ROUTES.SETTINGS);

  const handleServiceRateTypeChange = (
    serviceUuid: string,
    rateType: PaymentSchemeRateType,
    value: number,
  ) => {
    // Клампинг (например, до 100% при переключении на "Процент") уже
    // сделан в самом редакторе — здесь просто фиксируем то же значение,
    // что видно в поле.
    setServiceRows((rows) =>
      rows.map((row) =>
        row.service_uuid === serviceUuid
          ? { ...row, rate_type: rateType, value: String(value) }
          : row,
      ),
    );
  };

  const handleServiceValueBlur = (
    serviceUuid: string,
    rateType: PaymentSchemeRateType,
    value: number | null,
  ) => {
    setServiceRows((rows) =>
      rows.map((row) =>
        row.service_uuid === serviceUuid
          ? {
              ...row,
              rate_type: rateType,
              value: value === null ? null : String(value),
            }
          : row,
      ),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError("Обязательное поле");
      return;
    }
    if (!serviceCenterUuid) return;

    const services = serviceRows
      .filter((row) => row.rate_type && row.value !== null && row.value !== "")
      .map((row) => ({
        service_uuid: row.service_uuid,
        rate_type: row.rate_type as PaymentSchemeRateType,
        value: Number(row.value),
      }));

    const payload = {
      name: name.trim(),
      fix_per_shift:
        fixPerShift === null || fixPerShift === "" ? null : Number(fixPerShift),
      hourly_rate:
        hourlyRate === null || hourlyRate === "" ? null : Number(hourlyRate),
      services,
    };

    if (isCreate) {
      const success = await paymentSchemeTemplatesStore.createTemplate({
        ...payload,
        service_center_uuid: serviceCenterUuid,
      });
      if (success) navigate(ROUTES.SETTINGS);
      return;
    }

    // Шаблон уже назначен сотрудникам — сохранение требует явного пролива
    // изменений на них (см. handleConfirmSave), поэтому предупреждаем и
    // спрашиваем подтверждение перед сохранением.
    if (activeTemplate && activeTemplate.employees_count > 0) {
      setPendingSavePayload(payload);
      return;
    }

    const success = await paymentSchemeTemplatesStore.updateTemplate(
      uuid as string,
      payload,
    );
    if (!success) return;
    toastStore.showSuccess("Шаблон сохранён");
    navigate(ROUTES.SETTINGS);
  };

  const handleConfirmSave = async () => {
    if (!uuid || !pendingSavePayload) return;
    const success = await paymentSchemeTemplatesStore.updateTemplate(
      uuid,
      pendingSavePayload,
    );
    setPendingSavePayload(null);
    if (!success) return;
    // Сам PATCH шаблона сотрудников не трогает — пролив запускаем отдельным
    // запросом сразу после успешного сохранения.
    await paymentSchemeTemplatesStore.propagateTemplate(uuid);
    navigate(ROUTES.SETTINGS);
  };

  return (
    <div className="payment-scheme-template-edit-page" data-testid="payment-scheme-template-edit-page">
      <div className="payment-scheme-template-edit-page__container">
        <div className="payment-scheme-template-edit-page__header">
          <div className="payment-scheme-template-edit-page__header-left">
            <button
              type="button"
              className="payment-scheme-template-edit-page__back-button"
              onClick={handleCancel}
              aria-label="Назад"
              data-testid="template-edit-back-button"
            >
              <ArrowLeftIcon size={14} />
            </button>
            <h1 className="payment-scheme-template-edit-page__title">
              {isCreate
                ? "Создание шаблона оплаты"
                : "Редактирование шаблона оплаты"}
            </h1>
          </div>
          <div className="payment-scheme-template-edit-page__header-actions">
            <AppButton
              variant="secondary"
              onClick={handleCancel}
              data-testid="template-edit-cancel"
            >
              Отменить
            </AppButton>
            <AppButton
              variant="primary"
              iconRight={<FloppyDiskIcon size={10} />}
              onClick={handleSave}
              loading={paymentSchemeTemplatesStore.isSaving}
              data-testid="template-edit-save"
            >
              Сохранить
            </AppButton>
          </div>
        </div>

        <section className="payment-scheme-template-edit-page__section">
          <h2 className="payment-scheme-template-edit-page__section-title">
            Выбрать шаблон
          </h2>
          <p className="payment-scheme-template-edit-page__section-hint">
            Создайте новый или редактируйте существующие.
          </p>
          {paymentSchemeTemplatesStore.submitError && (
            <p className="payment-scheme-template-edit-page__submit-error">
              {paymentSchemeTemplatesStore.submitError}
            </p>
          )}
          <div className="payment-scheme-template-edit-page__template-picker">
            <AppAutocomplete
              value={autocompleteValue}
              options={templateOptions}
              onChange={handleTemplatePick}
              placeholder="Без шаблона"
              error={nameError}
              required
              data-testid="template-edit-name-autocomplete"
            />
          </div>
        </section>

        <section className="payment-scheme-template-edit-page__section">
          <PaymentSchemeEditor
            fixPerShift={fixPerShift}
            hourlyRate={hourlyRate}
            services={serviceRows}
            onFixPerShiftBlur={(value) =>
              setFixPerShift(value === null ? null : String(value))
            }
            onHourlyRateBlur={(value) =>
              setHourlyRate(value === null ? null : String(value))
            }
            onServiceRateTypeChange={handleServiceRateTypeChange}
            onServiceValueBlur={handleServiceValueBlur}
          />
        </section>
      </div>

      <div className="payment-scheme-template-edit-page__propagate-dialog">
        <AppConfirmDialog
          isOpen={!!pendingSavePayload}
          title="Хотите обновить шаблон оплаты?"
          message={
            <>
              Изменить шаблон и условия сотрудников,
              <br />
              к которым применён этот же шаблон
            </>
          }
          confirmText="Да"
          cancelText="Нет"
          onConfirm={handleConfirmSave}
          onCancel={() => setPendingSavePayload(null)}
          isConfirming={paymentSchemeTemplatesStore.isSaving}
          confirmingText="Сохранение..."
          dismissOnOverlayClick={false}
          data-testid="template-propagate-confirm-modal"
        />
      </div>
    </div>
  );
});

PaymentSchemeTemplateEditPage.displayName = "PaymentSchemeTemplateEditPage";

export default PaymentSchemeTemplateEditPage;
